<template>
  <div class="wheel-display-mode-field">
    <label v-if="configs.label" class="block mb-2">{{ t(configs.label) }}</label>
    <div class="mode-options">
      <button
        v-for="mode in displayModes"
        :key="mode.value"
        class="mode-option"
        :class="{ 'is-selected': selectedMode === mode.value }"
        :title="t(mode.label)"
        @click="selectMode(mode.value)"
      >
        <svg viewBox="0 0 40 40" class="mode-icon">
          <!-- Full circle -->
          <circle
            v-if="mode.value === 'full'"
            cx="20"
            cy="20"
            r="16"
            fill="currentColor"
          />
          <!-- Top half -->
          <path
            v-else-if="mode.value === 'top'"
            d="M 4 20 A 16 16 0 0 1 36 20 Z"
            fill="currentColor"
          />
          <!-- Bottom half -->
          <path
            v-else-if="mode.value === 'bottom'"
            d="M 4 20 A 16 16 0 0 0 36 20 Z"
            fill="currentColor"
          />
          <!-- Left half -->
          <path
            v-else-if="mode.value === 'left'"
            d="M 20 4 A 16 16 0 0 0 20 36 Z"
            fill="currentColor"
          />
          <!-- Right half -->
          <path
            v-else-if="mode.value === 'right'"
            d="M 20 4 A 16 16 0 0 1 20 36 Z"
            fill="currentColor"
          />
        </svg>
        <span class="mode-label">{{ t(mode.label) }}</span>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  watch,
  onMounted,
} from 'vue';
import { useI18n } from 'vue-i18n';
import {
  FieldConfig,
  ParserFieldObject,
  Property,
} from '@/types';

export default defineComponent({
  name: 'WheelDisplayModeField',

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: ['on-change-properties'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const property = ref<Property>(props.parserValues.properties[0]);
    const selectedMode = ref<string>('full');

    const displayModes = [
      { value: 'full', label: 'templateBuilder.games.wheelDisplayMode.full' },
      { value: 'top', label: 'templateBuilder.games.wheelDisplayMode.top' },
      { value: 'bottom', label: 'templateBuilder.games.wheelDisplayMode.bottom' },
      { value: 'left', label: 'templateBuilder.games.wheelDisplayMode.left' },
      { value: 'right', label: 'templateBuilder.games.wheelDisplayMode.right' },
    ];

    const parseInitialValue = () => {
      const value = property.value.value;
      if (value && typeof value === 'string' && displayModes.some(m => m.value === value)) {
        selectedMode.value = value;
      } else {
        selectedMode.value = 'full';
      }
    };

    const emitChange = () => {
      property.value.value = selectedMode.value;
      emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: [property.value],
      });
    };

    const selectMode = (mode: string) => {
      selectedMode.value = mode;
      emitChange();
    };

    onMounted(() => {
      parseInitialValue();
    });

    watch(() => props.parserValues.properties[0]?.value, () => {
      parseInitialValue();
    }, { deep: true });

    return {
      t,
      selectedMode,
      displayModes,
      selectMode,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/assets/scss/utilities/_variables.scss';

.wheel-display-mode-field {
  width: 100%;

  .mode-options {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .mode-option {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    padding: 0.5rem;
    border: 2px solid $solitude;
    border-radius: 6px;
    background: $white;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 60px;

    &:hover {
      border-color: $brand-color-primary;
    }

    &.is-selected {
      border-color: $brand-color-primary;
      background: rgba($brand-color-primary, 0.1);

      .mode-icon {
        color: $brand-color-primary;
      }
    }

    .mode-icon {
      width: 32px;
      height: 32px;
      color: $mid-grey;
      transition: color 0.2s ease;
    }

    .mode-label {
      font-size: 0.7rem;
      color: $mid-grey;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
  }
}
</style>
